import React from 'react';
import { StanButton, StanCalendar } from '@/components';
import { useDateFilter } from '../hooks/useDateFilter';
import { useFloatingFilterMenu } from '../hooks/useFloatingFilterMenu';
import { BaseFilter, BaseFilterProps } from '../components/BaseFilter';
import styles from '../StanFilters.module.css';
import dropdownStyles from '../../StanDropdown/Menu/StanDropdownMenu.module.css';

type Props = BaseFilterProps;

/**
 * Date filter component for filtering date-based fields
 * Supports both preset date ranges and custom calendar selection
 */
const DateFilter: React.FC<Props> = ({ id, label, filterItem, onApply, onClearFilter, disabled, placement }) => {
    const { menuRef, triggerProps, isOpen, handleOpenChange, closeMenu } = useFloatingFilterMenu(disabled);

    const {
        summary,
        hasSummary,
        from,
        to,
        apply,
        clearFilters,
        // Extracted complex logic from hook
        viewMode,
        presetOptions,
        getSelectedPreset,
        isCustomRange,
        selectedRangeText,
        handlePresetSelect,
        handleAbsoluteDatesClick,
        handleBackToPresets,
        handleRangeSelect,
    } = useDateFilter({
        filterItem,
        onApply,
        onClearFilter,
        disabled,
        closeMenu,
    });

    return (
        <BaseFilter
            id={id}
            label={label}
            filterItem={filterItem}
            onApply={onApply}
            onClearFilter={onClearFilter}
            disabled={disabled}
            placement={placement}
            menuRef={menuRef}
            triggerProps={triggerProps}
            summary={summary}
            hasSummary={hasSummary}
            isOpen={isOpen}
            handleOpenChange={handleOpenChange}
            clearFilters={clearFilters}
            menuClassName={`${styles.dateFilterMenu} ${styles.dateFilterDropdown}`}
        >
            <div className='flex flex-col items-center gap-3 p-4'>
                {viewMode === 'presets' ? (
                    <div className='w-full'>
                        <div className={dropdownStyles.list}>
                            {presetOptions.map(preset => {
                                const isSelected = getSelectedPreset()?.value === preset.value;
                                return (
                                    <button
                                        key={preset.value}
                                        type='button'
                                        className={`${dropdownStyles.item} ${isSelected ? dropdownStyles.selected : ''}`}
                                        onClick={() => handlePresetSelect(preset)}
                                        disabled={disabled}
                                    >
                                        <div className={dropdownStyles.itemContent}>
                                            <span className={dropdownStyles.itemLabel}>{preset.label}</span>
                                        </div>
                                    </button>
                                );
                            })}
                            <button
                                type='button'
                                className={`${dropdownStyles.item} ${isCustomRange ? dropdownStyles.selected : ''}`}
                                onClick={handleAbsoluteDatesClick}
                                disabled={disabled}
                            >
                                <div className={dropdownStyles.itemContent}>
                                    <div className='flex w-full flex-col items-start'>
                                        <span className={dropdownStyles.itemLabel}>Absolute dates →</span>
                                        {isCustomRange && (
                                            <div className='text-neutral-fg-secondary mt-1 text-left text-xs font-normal'>
                                                {selectedRangeText}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className='flex w-full flex-col gap-4'>
                        <div className='flex w-full flex-col items-center justify-center'>
                            <StanCalendar
                                analyticsName={`${filterItem.id}-date-filter`}
                                mode='range'
                                className='!pt-0'
                                selected={from || to ? { from, to } : undefined}
                                onSelect={handleRangeSelect}
                            />
                        </div>
                        <div className='mt-3 flex justify-between gap-2'>
                            <StanButton
                                type='button'
                                variant='outline'
                                onClick={handleBackToPresets}
                                className='w-full'
                                name='back-to-presets'
                            >
                                <span>Back</span>
                            </StanButton>
                            <StanButton type='button' className='w-full' onClick={apply} name='apply-date-filter'>
                                <span>Apply</span>
                            </StanButton>
                        </div>
                    </div>
                )}
            </div>
        </BaseFilter>
    );
};

export default DateFilter;
